import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Star, Trash2, Shield, BookOpen, PlayCircle, Tv, Calendar,
  ChevronDown, Loader2, AlertTriangle, Clock, CheckCircle, PauseCircle,
  XCircle, Bookmark, RefreshCw, ImageOff
} from 'lucide-react';
import api, { userApi } from '../api';

const PRIVACY_OPTIONS = [
  { value: 'default', label: 'Par défaut', desc: 'Hérite des paramètres globaux' },
  { value: 'public', label: 'Public', desc: 'Visible par tout le monde' },
  { value: 'friends', label: 'Amis', desc: 'Visible uniquement par vos amis' },
  { value: 'private', label: 'Privé', desc: 'Visible uniquement par vous' },
];

const STATUS_ICONS = {
  watching: <PlayCircle size={14} className="text-blue-400" />,
  completed: <CheckCircle size={14} className="text-green-400" />,
  on_hold: <PauseCircle size={14} className="text-orange-400" />,
  dropped: <XCircle size={14} className="text-red-400" />,
  plan_to_watch: <Bookmark size={14} className="text-indigo-400" />,
};

const STATUS_LABELS = {
  watching: 'En cours',
  completed: 'Terminé',
  on_hold: 'En pause',
  dropped: 'Abandonné',
  plan_to_watch: 'À voir',
};

function StarRating({ value }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={12} className={i <= value ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'} />
      ))}
    </div>
  );
}

const AnimeDetailsDrawer = ({ anime, onClose, onAnimeDeleted, onAnimeUpdated, user }) => {
  const [watchedEpisodes, setWatchedEpisodes] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [deleting, setDeleting] = useState(null); // ep_num being deleted
  const [deletingAnime, setDeletingAnime] = useState(false);
  const [privacyLevel, setPrivacyLevel] = useState(anime?.privacy_level || 'default');
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(anime?.mal_status || 'watching');
  const [savingStatus, setSavingStatus] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [imgError, setImgError] = useState(false);

  const isMalConnected = user?.mal_connected && anime?.mal_id;

  // Fetch watched episodes for this anime
  const fetchHistory = useCallback(async () => {
    if (!anime) return;
    setLoadingHistory(true);
    try {
      const res = await api.get(`/users/anime/${anime.id}`);
      setWatchedEpisodes(res.data.watched_ep_list || []);
    } catch (e) {
      console.error('Error fetching anime history:', e);
    } finally {
      setLoadingHistory(false);
    }
  }, [anime]);

  useEffect(() => {
    if (anime) {
      fetchHistory();
      setPrivacyLevel(anime.privacy_level || 'default');
      setCurrentStatus(anime.mal_status || 'watching');
      setImgError(false);
    }
  }, [anime, fetchHistory]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleDeleteEpisode = async (epNum) => {
    if (!window.confirm(`Supprimer tout l'historique de l'épisode ${epNum} ?`)) return;
    setDeleting(epNum);
    try {
      await api.delete(`/history/episode/${anime.id}/${epNum}`);
      await fetchHistory();
    } catch (e) {
      console.error('Error deleting episode:', e);
      alert('Erreur lors de la suppression.');
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteAnime = async (syncMal = false) => {
    setDeletingAnime(true);
    setShowDeleteModal(false);
    try {
      await api.delete(`/history/anime/${anime.id}?sync_mal=${syncMal}`);
      if (onAnimeDeleted) onAnimeDeleted(anime.id);
      onClose();
    } catch (e) {
      console.error('Error deleting anime:', e);
      alert('Erreur lors de la suppression.');
    } finally {
      setDeletingAnime(false);
    }
  };

  const handlePrivacyChange = async (newLevel) => {
    setPrivacyLevel(newLevel);
    setSavingPrivacy(true);
    try {
      await api.put(`/users/anime/${anime.id}/privacy`, { privacy_level: newLevel });
    } catch (e) {
      console.error('Error updating privacy:', e);
    } finally {
      setSavingPrivacy(false);
    }
  };

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    setCurrentStatus(newStatus);
    setSavingStatus(true);
    try {
      await api.put(`/users/anime/${anime.id}/status`, { mal_status: newStatus });
      if (onAnimeUpdated) {
        onAnimeUpdated(anime.id, { mal_status: newStatus });
      }
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setSavingStatus(false);
    }
  };

  if (!anime) return null;

  const displayTitle = anime.french || anime.english || anime.romaji;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-xl bg-gray-900 border-l border-gray-700/60 shadow-2xl flex flex-col overflow-hidden"
        style={{ animation: 'slideInRight 0.25s ease-out' }}>

        {/* Header */}
        <div className="relative h-48 flex-shrink-0 overflow-hidden">
          {anime.cover_image_url && !imgError ? (
            <img
              src={anime.cover_image_url}
              alt={displayTitle}
              className="w-full h-full object-cover object-top opacity-40"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="w-full h-full bg-gray-800 flex items-center justify-center">
              <ImageOff size={48} className="text-gray-600" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent" />

          <button
            onClick={onClose}
            className="absolute top-4 right-4 bg-gray-800/80 hover:bg-gray-700 p-2 rounded-full text-gray-300 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>

          <div className="absolute bottom-0 left-0 right-0 p-5 flex gap-4">
            {anime.cover_image_url && !imgError ? (
              <img
                src={anime.cover_image_url}
                alt={displayTitle}
                className="w-20 h-28 object-cover rounded-lg shadow-xl border border-gray-700/50 flex-shrink-0"
              />
            ) : (
              <div className="w-20 h-28 bg-gray-800 rounded-lg border border-gray-700/50 flex items-center justify-center flex-shrink-0">
                <ImageOff size={28} className="text-gray-600" />
              </div>
            )}
            <div className="flex flex-col justify-end pb-1">
              <h2 className="text-xl font-bold text-white leading-tight line-clamp-2">{displayTitle}</h2>
              {anime.romaji && anime.french && (
                <p className="text-sm text-gray-400 mt-0.5">{anime.romaji}</p>
              )}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {currentStatus && (
                  <div className="relative flex items-center gap-1 text-xs pl-2 pr-1 py-1 bg-gray-800/80 rounded-full border border-gray-700/50 hover:border-gray-500 transition-colors">
                    {savingStatus ? <Loader2 size={14} className="animate-spin text-gray-400" /> : STATUS_ICONS[currentStatus]}
                    <select
                      value={currentStatus}
                      onChange={handleStatusChange}
                      disabled={savingStatus}
                      className="bg-transparent text-gray-300 font-medium appearance-none outline-none cursor-pointer pr-4 hover:text-white disabled:opacity-50"
                      title="Changer le statut"
                    >
                      {Object.entries(STATUS_LABELS).map(([key, label]) => (
                        <option key={key} value={key} className="bg-gray-800 text-white">{label}</option>
                      ))}
                    </select>
                    <ChevronDown size={12} className="text-gray-500 absolute right-2 pointer-events-none" />
                  </div>
                )}
                {anime.rating && (
                  <span className="flex items-center gap-1 text-xs px-2 py-1 bg-yellow-400/10 rounded-full border border-yellow-400/20 text-yellow-400">
                    <Star size={10} className="fill-yellow-400" />
                    {anime.rating}/5
                  </span>
                )}
                {anime.total_episodes > 0 && (
                  <span className="flex items-center gap-1 text-xs px-2 py-1 bg-gray-800/80 rounded-full border border-gray-700/50 text-gray-400">
                    <Tv size={10} />
                    {anime.episodes_watched}/{anime.total_episodes} eps
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {/* Privacy section */}
          <div className="bg-gray-800/60 rounded-xl p-4 border border-gray-700/50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-200 flex items-center gap-2">
                <Shield size={14} className="text-blue-400" />
                Confidentialité de cet anime
              </h3>
              {savingPrivacy && <Loader2 size={14} className="animate-spin text-blue-400" />}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {PRIVACY_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => handlePrivacyChange(opt.value)}
                  className={`text-left p-2.5 rounded-lg border text-xs transition-all ${
                    privacyLevel === opt.value
                      ? 'border-blue-500/60 bg-blue-500/10 text-blue-300'
                      : 'border-gray-700/50 bg-gray-800/40 text-gray-400 hover:border-gray-600 hover:text-gray-300'
                  }`}
                >
                  <div className="font-semibold">{opt.label}</div>
                  <div className="opacity-70 mt-0.5">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Episodes history */}
          <div>
            <h3 className="text-sm font-semibold text-gray-200 flex items-center gap-2 mb-3">
              <BookOpen size={14} className="text-blue-400" />
              Épisodes regardés
              {!loadingHistory && (
                <span className="text-xs text-gray-500 font-normal">({watchedEpisodes.length} épisodes)</span>
              )}
              <button onClick={fetchHistory} className="ml-auto text-gray-500 hover:text-gray-300 transition-colors" title="Rafraîchir">
                <RefreshCw size={12} />
              </button>
            </h3>

            {loadingHistory ? (
              <div className="flex justify-center py-8">
                <Loader2 size={24} className="animate-spin text-blue-400" />
              </div>
            ) : watchedEpisodes.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                Aucun épisode regardé enregistré.
              </div>
            ) : (
              <div className="space-y-2">
                {[...watchedEpisodes].sort((a, b) => b.episode.ep_num - a.episode.ep_num).map((ep) => (
                  <div
                    key={ep.episode.ep_num}
                    className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-xl border border-gray-700/40 hover:border-gray-600/60 transition-colors group"
                  >
                    <div className="w-10 h-10 flex-shrink-0 bg-gray-700/60 rounded-lg flex items-center justify-center text-sm font-bold text-white">
                      {ep.episode.ep_num}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-200 font-medium">Épisode {ep.episode.ep_num}</span>
                        {ep.episode.rating && <StarRating value={ep.episode.rating} />}
                      </div>
                      {ep.watched_at && ep.watched_at[0] && (
                        <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                          <Clock size={10} />
                          {new Date(ep.watched_at[0]).toLocaleDateString('fr-FR', {
                            day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                          {ep.watched_at.length > 1 && (
                            <span className="ml-1 text-gray-600">· vu {ep.watched_at.length}×</span>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteEpisode(ep.episode.ep_num)}
                      disabled={deleting === ep.episode.ep_num}
                      className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-red-400/70 hover:text-red-400 hover:bg-red-400/10 transition-all flex-shrink-0"
                      title={`Supprimer l'épisode ${ep.episode.ep_num}`}
                    >
                      {deleting === ep.episode.ep_num
                        ? <Loader2 size={14} className="animate-spin" />
                        : <Trash2 size={14} />}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer — danger zone */}
        <div className="flex-shrink-0 p-4 border-t border-gray-700/50 bg-gray-900/80">
          <button
            onClick={() => setShowDeleteModal(true)}
            disabled={deletingAnime}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-xl border border-red-500/20 hover:border-red-500/40 transition-all text-sm font-semibold"
          >
            {deletingAnime ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
            Supprimer cet anime de ma liste
          </button>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-2xl border border-gray-700 p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-red-500/10 rounded-full flex items-center justify-center">
                <AlertTriangle size={20} className="text-red-400" />
              </div>
              <h3 className="text-lg font-bold text-white">Supprimer « {displayTitle} » ?</h3>
            </div>
            <p className="text-gray-400 text-sm mb-5 leading-relaxed">
              Cela supprimera tout votre historique de visionnage, vos notes et le statut de suivi pour cet anime.
              Cette action est <strong className="text-white">irréversible</strong>.
            </p>
            <div className="space-y-2">
              {isMalConnected && (
                <button
                  onClick={() => handleDeleteAnime(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors text-sm font-semibold"
                >
                  <Trash2 size={16} />
                  Supprimer + Retirer de MyAnimeList
                </button>
              )}
              <button
                onClick={() => handleDeleteAnime(false)}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl transition-colors text-sm font-semibold ${
                  isMalConnected
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
              >
                <Trash2 size={16} />
                {isMalConnected ? 'Supprimer uniquement sur MyAnimeTracker' : 'Confirmer la suppression'}
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="w-full px-4 py-2.5 text-gray-400 hover:text-white rounded-xl hover:bg-gray-700 transition-colors text-sm"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default AnimeDetailsDrawer;
